// Account-local wall time to an ISO instant with the correct offset, using
// only Intl (no date library). The server rechecks every instant; this only
// lets the operator pick a time in the account's zone and see it honestly.

export type ZonedResolution =
  | { kind: "ok"; iso: string; ms: number; offsetMinutes: number }
  | { kind: "ambiguous"; iso: string; ms: number; offsetMinutes: number; laterIso: string }
  | { kind: "nonexistent"; before: string; after: string };

const pad = (n: number, w = 2) => String(Math.abs(n)).padStart(w, "0");

const partsCache = new Map<string, Intl.DateTimeFormat>();
const formatterFor = (timeZone: string): Intl.DateTimeFormat => {
  let f = partsCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    } as Intl.DateTimeFormatOptions);
    partsCache.set(timeZone, f);
  }
  return f;
};

/** Wall clock fields of an instant in a zone. */
export const wallClock = (ms: number, timeZone: string) => {
  const parts: Record<string, number> = {};
  for (const p of formatterFor(timeZone).formatToParts(new Date(ms))) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour === 24 ? 0 : parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
};

/** Offset of the zone at an instant, in minutes east of UTC. */
export const offsetMinutesAt = (ms: number, timeZone: string): number => {
  const w = wallClock(ms, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  const floored = Math.floor(ms / 1000) * 1000;
  return Math.round((asUtc - floored) / 60000);
};

export const formatOffset = (minutes: number): string => {
  if (minutes === 0) return "+00:00";
  const sign = minutes > 0 ? "+" : "-";
  return `${sign}${pad(Math.floor(Math.abs(minutes) / 60))}:${pad(Math.abs(minutes) % 60)}`;
};

/** ISO 8601 with the zone's offset at that instant, e.g. 2026-10-10T19:00:00-04:00. */
export const isoInZone = (ms: number, timeZone: string): string => {
  const w = wallClock(ms, timeZone);
  const off = offsetMinutesAt(ms, timeZone);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}:${pad(w.second)}${formatOffset(off)}`;
};

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;

/**
 * Resolves a local date and time in a zone. A time skipped by a DST change is
 * "nonexistent" (the caller must pick another). A repeated time is
 * "ambiguous": the earlier instant is chosen, the later one is reported.
 */
export const resolveLocalTime = (localDate: string, localTime: string, timeZone: string): ZonedResolution => {
  const d = DATE_RE.exec(localDate);
  const t = TIME_RE.exec(localTime);
  if (!d || !t) throw new Error("Use a date like 2026-10-10 and a time like 19:00.");
  const [y, mo, da] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [h, mi] = [Number(t[1]), Number(t[2])];
  const wallAsUtc = Date.UTC(y, mo - 1, da, h, mi, 0);
  const DAY = 24 * 60 * 60 * 1000;
  const offsets = new Set<number>([
    offsetMinutesAt(wallAsUtc - DAY, timeZone),
    offsetMinutesAt(wallAsUtc, timeZone),
    offsetMinutesAt(wallAsUtc + DAY, timeZone),
  ]);
  const valid: { ms: number; off: number }[] = [];
  offsets.forEach((off) => {
    const ms = wallAsUtc - off * 60000;
    if (offsetMinutesAt(ms, timeZone) === off) valid.push({ ms, off });
  });
  valid.sort((a, b) => a.ms - b.ms);
  if (valid.length === 0) {
    // The same wall time read with each side's offset lands one gap length
    // before and after the skipped hour.
    const offs = Array.from(offsets).sort((a, b) => a - b);
    return {
      kind: "nonexistent",
      before: isoInZone(wallAsUtc - offs[offs.length - 1] * 60000, timeZone),
      after: isoInZone(wallAsUtc - offs[0] * 60000, timeZone),
    };
  }
  const first = valid[0];
  const iso = `${d[1]}-${d[2]}-${d[3]}T${t[1]}:${t[2]}:00${formatOffset(first.off)}`;
  if (valid.length > 1) {
    const last = valid[valid.length - 1];
    return {
      kind: "ambiguous",
      iso,
      ms: first.ms,
      offsetMinutes: first.off,
      laterIso: `${d[1]}-${d[2]}-${d[3]}T${t[1]}:${t[2]}:00${formatOffset(last.off)}`,
    };
  }
  return { kind: "ok", iso, ms: first.ms, offsetMinutes: first.off };
};

/** Human label, e.g. "Sat, Oct 10, 18:00 (America/New_York)". */
export const formatInZone = (msOrIso: number | string, timeZone: string): string => {
  const ms = typeof msOrIso === "number" ? msOrIso : Date.parse(msOrIso);
  if (!Number.isFinite(ms)) return "unknown time";
  try {
    const s = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    } as Intl.DateTimeFormatOptions).format(new Date(ms));
    return `${s} (${timeZone})`;
  } catch {
    return new Date(ms).toISOString();
  }
};

/** Local date and time ("YYYY-MM-DD", "HH:mm") of an instant in a zone. */
export const localPartsInZone = (ms: number, timeZone: string): { date: string; time: string } => {
  const w = wallClock(ms, timeZone);
  return { date: `${w.year}-${pad(w.month)}-${pad(w.day)}`, time: `${pad(w.hour)}:${pad(w.minute)}` };
};

export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const FALLBACK_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "America/Argentina/Buenos_Aires",
  "America/Sao_Paulo",
  "America/Managua",
  "America/Panama",
  "America/Toronto",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Lisbon",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

/** IANA zones the browser knows, with a short fallback list. */
export const ianaTimeZones = (): string[] => {
  const fn = (Intl as any).supportedValuesOf;
  if (typeof fn === "function") {
    try {
      const list = fn.call(Intl, "timeZone") as string[];
      if (Array.isArray(list) && list.length > 0) return list.includes("UTC") ? list : ["UTC", ...list];
    } catch {
      // fall through
    }
  }
  return FALLBACK_ZONES;
};
